import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

import { mergeObjects, removeObjectProperty } from "../utils";
import { firestore } from "../services/firebase";
import { useNotification } from "./notification";
import { Training } from "../@data";
import { useAuth } from "./auth";

export interface TrainingContextModel {
  training: Training | null;
  loadingTraining: boolean;
  handleSaveTraining: (
    selectedDay: string,
    trainingType: string,
    description: string,
    successCallback?: () => void
  ) => void;
  handleSaveTrainingSheet: (
    newTraining: Training,
    successCallback?: () => void
  ) => void;
  saving: boolean;
  handleDeleteTraining: (
    selectedDay: string,
    trainingType: string,
    successCallback?: () => void
  ) => void;
  deleting: boolean;
}

export const TrainingContext = createContext<TrainingContextModel>(
  {} as TrainingContextModel
);

export const TrainingProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { user } = useAuth();
  const { handleNotification } = useNotification();
  const [training, setTraining] =
    useState<TrainingContextModel["training"]>(null);
  const [loadingTraining, setLoadingTraining] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleGetTraining = useCallback(async () => {
    getDoc(doc(firestore, `trainings/${user.uid}`))
      .then((snapshot) => {
        setTraining(snapshot.data() as Training);
      })
      .finally(() => setLoadingTraining(false));
  }, [user.uid]);

  const handleSaveTraining: TrainingContextModel["handleSaveTraining"] =
    useCallback(
      (selectedDay, trainingType, description, successCallback) => {
        let newTraining = {
          [selectedDay]: {
            [trainingType as string]: description.trim(),
          },
        };
        if (training && Object.values(training).length)
          newTraining = mergeObjects(training as never, newTraining);
        setSaving(true);
        setDoc(doc(firestore, "trainings", user.uid), newTraining)
          .then(() => {
            handleNotification("success", "Treino salvo com sucesso!");
            successCallback && successCallback();
            handleGetTraining();
          })
          .catch(() =>
            handleNotification(
              "error",
              "Ocorreu um erro ao tentar salvar seu treino!"
            )
          )
          .finally(() => setSaving(false));
      },
      [handleNotification, training, user.uid, handleGetTraining]
    );

  const handleSaveTrainingSheet: TrainingContextModel["handleSaveTrainingSheet"] =
    useCallback(
      (newTraining, successCallback) => {
        setSaving(true);
        updateDoc(doc(firestore, "trainings", user.uid), newTraining as never)
          .then(() => {
            handleNotification(
              "success",
              "Data dos treinos salva com sucesso!"
            );
            handleGetTraining();
            successCallback && successCallback();
          })
          .catch(() =>
            handleNotification(
              "error",
              "Ocorreu um erro ao tentar salvar sua data de treino!"
            )
          )
          .finally(() => setSaving(false));
      },
      [handleNotification, user.uid, handleGetTraining]
    );

  const handleDeleteTraining: TrainingContextModel["handleDeleteTraining"] =
    useCallback(
      (selectedDay, trainingType, successCallback) => {
        if (training) {
          let newTraining = removeObjectProperty(
            training as never,
            selectedDay,
            trainingType
          );
          setDeleting(true);
          setDoc(doc(firestore, "trainings", user.uid), newTraining)
            .then(() => {
              handleNotification("success", "Treino excluído com sucesso!");
              successCallback && successCallback();
              handleGetTraining();
            })
            .catch(() =>
              handleNotification(
                "error",
                "Ocorreu um erro ao tentar excluir seu treino!"
              )
            )
            .finally(() => setDeleting(false));
        }
      },
      [handleNotification, training, user.uid, handleGetTraining]
    );

  useEffect(() => {
    handleGetTraining();
  }, [handleGetTraining]);

  return (
    <TrainingContext.Provider
      value={{
        training,
        loadingTraining,
        handleSaveTraining,
        handleSaveTrainingSheet,
        saving,
        handleDeleteTraining,
        deleting,
      }}
    >
      {children}
    </TrainingContext.Provider>
  );
};

export const useTraining = () => useContext(TrainingContext);
